import React, { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactCountryFlag from "react-country-flag";
import { AiModal } from "../Quote/Leads";
import { getFileUrl } from "../../../core/config/api.js";
import { conditionFormatter } from "../../../core/utils/formatters";
import useWhatsappMatch from "./useWhatsappMatch";
import "./lead-row.scss";

dayjs.extend(relativeTime);

const REGION_TO_COUNTRY = {
  uk: "GB", us: "US", europe: "EU", "middle-east": "AE", asia: "JP",
  australia: "AU", "north-america": "US", "south-america": "BR", africa: "ZA",
  germany: "DE", france: "FR", italy: "IT", spain: "ES", switzerland: "CH",
  netherlands: "NL", belgium: "BE", canada: "CA", japan: "JP", china: "CN",
  india: "IN", singapore: "SG", "hong-kong": "HK", uae: "AE", qatar: "QA", saudi: "SA",
};

const getCountryCode = (region) => {
  if (!region) return null;
  const normalized = region.toLowerCase().trim();
  if (REGION_TO_COUNTRY[normalized]) return REGION_TO_COUNTRY[normalized];
  if (normalized.length === 2) return normalized.toUpperCase();
  return null;
};

/**
 * The list view of a lead: one full-width row instead of a card.
 *
 * Same data and the same click target as `LeadsGridCard`, laid out for running
 * down a filtered list rather than reading one enquiry at a time. Roughly three
 * times the density of the card view.
 */
const LeadRow = ({ item, type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isOpportunityTab = type === "in-stock" || type === "in-consignment";

  const { matchData } = useWhatsappMatch(item.product_id, item.message_id);

  const productImage = item?.product?.images?.[0]?.id ? getFileUrl(item.product.images[0].id) : null;
  const brandName = item?.product?.brand?.name || "";
  const modelName = item?.product?.model?.name || "";
  const reference = item?.product?.reference_number || "";
  const year = item?.year || "";
  const condition = conditionFormatter(item?.condition) || "N/A";

  const senderName = matchData?.sender_name || "";
  const messageText = matchData?.message_text || "";
  const countryCode = getCountryCode(matchData?.region || item?.region || "");

  // Their photo first, your stock second — same rule as the card view.
  const theirImage = matchData?.whatsapp_image_url || null;
  const photo = theirImage || productImage;

  const askedAt = matchData?.message_date || item?.created_at;
  const askedLabel = askedAt ? dayjs(askedAt).fromNow() : "";

  return (
    <>
      <div
        className="lead-row"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen(true); } }}
      >
        <span className={`lead-row__photo${theirImage ? " is-theirs" : ""}`}>
          {photo ? (
            <>
              <img className="lead-row__photo-main" src={photo} alt={`${brandName} ${modelName}`.trim() || "Watch"} />
            </>
          ) : (
            <span className="lead-row__photo-empty">—</span>
          )}
          {theirImage && (
            <span className="lead-row__wamark">
              <img src="/assets/icons/whatsapp-green-circle.svg" alt="Photo sent on WhatsApp" />
            </span>
          )}
        </span>

        <span className="lead-row__mid">
          <span className="lead-row__top">
            <span className="lead-row__model">{modelName || brandName || "Listing"}</span>
            {reference && <span className="lead-row__ref">{reference}</span>}
          </span>
          <span className={`lead-row__want${messageText ? "" : " is-empty"}`}>
            {messageText
              ? <>{senderName && <b>{senderName}</b>}{senderName ? " — " : ""}{messageText}</>
              : "No message — matched on reference"}
          </span>
        </span>

        <span className="lead-row__right">
          <span className="lead-row__facts">
            {year && <span className="lead-row__fact">{year}</span>}
            <span className="lead-row__fact">{condition}</span>
            {askedLabel && <span className="lead-row__fact lead-row__fact--age">{askedLabel}</span>}
          </span>

          {countryCode && (
            <span className="lead-row__flag">
              <ReactCountryFlag countryCode={countryCode} svg />
            </span>
          )}

          {matchData && (
            <span className="lead-row__wa">
              <img src="/assets/icons/whatsapp-green-circle.svg" alt="From WhatsApp" />
            </span>
          )}
        </span>
      </div>

      <AiModal isOpen={isOpen} item={item} hide={() => setIsOpen(false)} isOpportunityModal={isOpportunityTab} approveBlack />
    </>
  );
};

export default LeadRow;
